import type { ReactNode } from 'react';
import { format } from 'date-fns';
import { decode } from 'he';
import DOMPurify from 'dompurify';
import { TopicVersion, TopicVersionSnapshot } from '../../types/graphql';
import { Badge } from '../ui/badge';

function toSafeHtml(raw?: string | null): string {
  if (!raw) return '';
  let decoded = raw;
  for (let i = 0; i < 3; i++) {
    const next = decode(decoded);
    if (next === decoded) break;
    decoded = next;
  }
  return DOMPurify.sanitize(decoded, { USE_PROFILES: { html: true } });
}

function plain(raw?: string | null): string {
  if (!raw) return '';
  const div = document.createElement('div');
  div.innerHTML = toSafeHtml(raw);
  return (div.textContent || '').replace(/\s+/g, ' ').trim();
}

function taxonomySummary(snap: TopicVersionSnapshot): string {
  const tax = snap.taxonomies || {};
  const parts = Object.entries(tax).map(([key, terms]) => {
    const labels = (terms || []).map((t) => t.term).filter(Boolean);
    return labels.length ? `${key}: ${labels.join(', ')}` : null;
  });
  return parts.filter(Boolean).join(' · ') || '—';
}

function entitiesSummary(snap: TopicVersionSnapshot): string {
  const a = snap.atoms || {};
  const n =
    (a.key_people?.length || 0) +
    (a.key_organizations?.length || 0) +
    (a.key_locations?.length || 0);
  const facts = a.verified_facts?.length || 0;
  return `${n} entities · ${facts} facts`;
}

type CompareField = {
  key: string;
  label: string;
  /** Normalized string used for equality / highlight. */
  valueOf: (snap: TopicVersionSnapshot) => string;
  /** Render cell content. */
  render: (snap: TopicVersionSnapshot) => ReactNode;
  /** Rich HTML block (summary/body). */
  rich?: boolean;
};

const FIELDS: CompareField[] = [
  {
    key: 'title',
    label: 'Title',
    valueOf: (s) => (s.title || '').trim(),
    render: (s) => s.title || '—',
  },
  {
    key: 'contentType',
    label: 'Content type',
    valueOf: (s) => (s.contentType || '').trim(),
    render: (s) => <span className="capitalize">{s.contentType || '—'}</span>,
  },
  {
    key: 'publishStatus',
    label: 'Publish status',
    valueOf: (s) => (s.publishStatus || '').trim(),
    render: (s) => <span className="capitalize">{s.publishStatus || '—'}</span>,
  },
  {
    key: 'publishedDate',
    label: 'Publish date',
    valueOf: (s) => (s.publishedDate ? String(s.publishedDate) : ''),
    render: (s) => (s.publishedDate ? format(new Date(s.publishedDate), 'PPpp') : '—'),
  },
  {
    key: 'category',
    label: 'Topic',
    valueOf: (s) => (s.category || '').trim(),
    render: (s) => s.category || '—',
  },
  {
    key: 'keywords',
    label: 'Tags',
    valueOf: (s) => (s.keywords || '').trim(),
    render: (s) => s.keywords || '—',
  },
  {
    key: 'destinationUrl',
    label: 'Public link',
    valueOf: (s) => (s.destinationUrl || '').trim(),
    render: (s) => (s.destinationUrl ? <span className="break-all">{s.destinationUrl}</span> : '—'),
  },
  {
    key: 'mediaUrl',
    label: 'Media',
    valueOf: (s) => (s.mediaUrl || '').trim(),
    render: (s) => (s.mediaUrl ? <span className="break-all">{s.mediaUrl}</span> : '—'),
  },
  {
    key: 'summary',
    label: 'Summary',
    valueOf: (s) => plain(s.summary),
    render: (s) =>
      s.summary ? (
        <div
          className="text-sm [&_p]:mb-2"
          dangerouslySetInnerHTML={{ __html: toSafeHtml(s.summary) }}
        />
      ) : (
        '—'
      ),
    rich: true,
  },
  {
    key: 'bodyText',
    label: 'Body',
    valueOf: (s) => plain(s.bodyText),
    render: (s) =>
      s.bodyText ? (
        <div
          className="text-sm max-h-80 overflow-auto [&_p]:mb-2"
          dangerouslySetInnerHTML={{ __html: toSafeHtml(s.bodyText) }}
        />
      ) : (
        '—'
      ),
    rich: true,
  },
  {
    key: 'taxonomies',
    label: 'Classification',
    valueOf: (s) => taxonomySummary(s),
    render: (s) => taxonomySummary(s),
  },
  {
    key: 'entities',
    label: 'Entities / facts',
    valueOf: (s) => entitiesSummary(s),
    render: (s) => entitiesSummary(s),
  },
];

function valuesDiffer(versions: TopicVersion[], field: CompareField): boolean {
  if (versions.length < 2) return false;
  const values = versions.map((v) => field.valueOf(v.snapshot || {}));
  return new Set(values).size > 1;
}

export function VersionCompareView({
  versions,
  onClose,
  articleTags = false,
}: {
  /** 2–3 versions, any order — displayed oldest → newest left to right. */
  versions: TopicVersion[];
  onClose: () => void;
  /**
   * A tinycms article keeps its tags on the article, not in these snapshots,
   * so the snapshot's keywords column is not its Tags and is left out.
   */
  articleTags?: boolean;
}) {
  const fields = articleTags ? FIELDS.filter((f) => f.key !== 'keywords') : FIELDS;
  const ordered = [...versions].sort((a, b) => a.versionNumber - b.versionNumber);
  const colClass =
    ordered.length === 2 ? 'grid-cols-[8rem_1fr_1fr]' : 'grid-cols-[8rem_1fr_1fr_1fr]';

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-white">Comparing versions</h4>
          <p className="text-xs text-cmp-ink-3 mt-0.5">
            Highlighted rows differ across the selected revisions.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-xs text-cmp-ink-2 hover:text-white underline"
        >
          Back to list
        </button>
      </div>

      <div className="overflow-x-auto border border-cmp-line rounded">
        {/* Column headers */}
        <div
          className={`grid ${colClass} gap-0 border-b border-cmp-line bg-form-dark/60 sticky top-0 z-10`}
        >
          <div className="p-3 text-xs text-cmp-ink-3">Field</div>
          {ordered.map((v) => (
            <div key={v.id} className="p-3 border-l border-cmp-line min-w-[14rem]">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-white font-medium">Version {v.versionNumber}</span>
                <Badge variant="outline" className="text-cmp-ink capitalize">
                  {v.changeType}
                </Badge>
              </div>
              <p className="text-xs text-cmp-ink-2 mt-1">
                {format(new Date(v.createdAt), 'PPp')}
                {v.createdByEmail ? ` · ${v.createdByEmail}` : ''}
              </p>
            </div>
          ))}
        </div>

        {fields.map((field) => {
          const differs = valuesDiffer(ordered, field);
          return (
            <div
              key={field.key}
              className={`grid ${colClass} gap-0 border-b border-cmp-line last:border-b-0 ${
                differs ? 'bg-warning/5' : ''
              }`}
            >
              <div
                className={`p-3 text-xs font-medium ${differs ? 'text-warning' : 'text-cmp-ink-2'}`}
              >
                {field.label}
                {differs && <span className="block text-[10px] font-normal mt-0.5">differs</span>}
              </div>
              {ordered.map((v) => (
                <div
                  key={v.id}
                  className={`p-3 border-l border-cmp-line text-sm text-cmp-ink min-w-[14rem] ${
                    differs ? 'ring-inset ring-1 ring-warning/30' : ''
                  }`}
                >
                  {field.render(v.snapshot || {})}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
